import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Shield, Loader2, AlertTriangle, Star } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const hwToken = () => new URLSearchParams(window.location.search).get("t") || "";
const withToken = (url) => `${url}${url.includes("?") ? "&" : "?"}t=${encodeURIComponent(hwToken())}`;

export default function CustomerAccept() {
  const { requestId } = useParams();
  const [jobInfo, setJobInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [pinError, setPinError] = useState("");
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [ratingSubmitted, setRatingSubmitted] = useState(false);
  const [submittingRating, setSubmittingRating] = useState(false);

  useEffect(() => {
    if (!requestId) return;
    (async () => {
      try {
        const res = await fetch(withToken(`${API_BASE}/api/requests/${requestId}/public-job-info`));
        const data = await res.json().catch(() => null);
        if (!res.ok) { setError(data?.detail || "Unable to load"); return; }
        if (data.customer_accepted) setAccepted(true);
        setJobInfo(data);
      } catch { setError("Unable to connect."); }
      finally { setLoading(false); }
    })();
  }, [requestId]);

  const handleAccept = async () => {
    if (pin.length < 4) { setPinError("Enter the 4-digit PIN"); return; }
    setPinError("");
    setConfirming(true);
    try {
      const res = await fetch(withToken(`${API_BASE}/api/requests/${requestId}/customer-accept`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) { setPinError(data?.detail || "Invalid PIN"); return; }
      setAccepted(true);
      setShowRating(true);
    } catch { setPinError("Connection error."); }
    finally { setConfirming(false); }
  };

  const submitRating = async () => {
    if (rating === 0) return;
    setSubmittingRating(true);
    try {
      await fetch(withToken(`${API_BASE}/api/ratings/${requestId}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment }),
      });
      setRatingSubmitted(true);
      setShowRating(false);
    } catch { }
    finally { setSubmittingRating(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
        <CardContent className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-white mb-2">Unable to Load</h2>
          <p className="text-sm text-zinc-400">{error}</p>
        </CardContent>
      </Card>
    </div>
  );

  if (accepted) return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm border-emerald-800 bg-zinc-900">
        <CardContent className="p-8 text-center space-y-4">
          <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h2 className="text-xl font-bold text-white">Job Accepted</h2>
          <p className="text-sm text-zinc-400">
            You have confirmed completion of the work by {jobInfo?.technician_name}.
          </p>

          {/* Rating Section */}
          {showRating && !ratingSubmitted && (
            <div className="pt-3 border-t border-zinc-800 space-y-3" data-testid="rating-section">
              <p className="text-sm font-medium text-white">Rate your technician</p>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map(s => (
                  <button key={s}
                    onMouseEnter={() => setHoverRating(s)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(s)}
                    data-testid={`star-${s}`}>
                    <Star className={`w-8 h-8 transition-colors ${
                      s <= (hoverRating || rating) ? "fill-amber-400 text-amber-400" : "text-zinc-600"
                    }`} />
                  </button>
                ))}
              </div>
              {rating > 0 && (
                <p className="text-xs text-amber-400 font-medium">
                  {rating === 5 ? "Excellent!" : rating === 4 ? "Great!" : rating === 3 ? "Good" : rating === 2 ? "Fair" : "Poor"}
                </p>
              )}
              <textarea value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Leave a comment (optional)..."
                rows={2}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-red-500"
                data-testid="rating-comment" />
              <Button onClick={submitRating} disabled={submittingRating || rating === 0}
                className="w-full h-10 bg-amber-500 hover:bg-amber-600 text-black font-medium"
                data-testid="submit-rating-btn">
                {submittingRating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Submit Rating"}
              </Button>
              <button onClick={() => { setShowRating(false); setRatingSubmitted(true); }}
                className="text-xs text-zinc-500 hover:text-zinc-400">Skip</button>
            </div>
          )}

          {ratingSubmitted && rating > 0 && (
            <div className="pt-3 border-t border-zinc-800">
              <div className="flex justify-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map(s => (
                  <Star key={s} className={`w-5 h-5 ${s <= rating ? "fill-amber-400 text-amber-400" : "text-zinc-600"}`} />
                ))}
              </div>
              <p className="text-xs text-zinc-400">Thank you for your feedback!</p>
            </div>
          )}

          <p className="text-xs text-zinc-500">Thank you for choosing Apex FieldProIQ.</p>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" data-testid="customer-accept-page">
      <Card className="w-full max-w-sm border-zinc-800 bg-zinc-900">
        <CardContent className="p-6 space-y-5">
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-3">
              <Shield className="w-7 h-7 text-red-400" />
            </div>
            <h1 className="text-lg font-bold text-white">Accept Job Completion</h1>
            <p className="text-xs text-zinc-400 mt-1">Apex FieldProIQ</p>
          </div>

          {jobInfo && (
            <div className="p-4 bg-zinc-800 rounded-xl space-y-2">
              <div>
                <p className="text-[10px] text-zinc-500 uppercase font-bold">Technician</p>
                <p className="text-sm text-white font-medium">{jobInfo.technician_name}</p>
              </div>
              <div className="border-t border-zinc-700 pt-2">
                <p className="text-[10px] text-zinc-500 uppercase font-bold">Service</p>
                <p className="text-sm text-white">{jobInfo.service_type}</p>
              </div>
              {jobInfo.completed_at && (
                <div className="border-t border-zinc-700 pt-2">
                  <p className="text-[10px] text-zinc-500 uppercase font-bold">Completed</p>
                  <p className="text-sm text-white">{new Date(jobInfo.completed_at).toLocaleString()}</p>
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <label className="text-xs text-zinc-400 font-medium block">
              Enter the 4-digit PIN from the technician:
            </label>
            <input
              type="text" inputMode="numeric" value={pin}
              onChange={(e) => { setPin(e.target.value.replace(/\D/g, "").slice(0, 4)); setPinError(""); }}
              placeholder="0000" maxLength={4}
              className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-center text-3xl font-mono text-white tracking-[0.4em] placeholder:text-zinc-600 focus:outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              data-testid="acceptance-pin-input"
            />
            {pinError && <p className="text-xs text-red-400 text-center" data-testid="pin-error">{pinError}</p>}
          </div>

          <Button onClick={handleAccept} disabled={confirming || pin.length < 4}
            className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-medium"
            data-testid="accept-job-submit">
            {confirming ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Accept & Confirm</>}
          </Button>

          <p className="text-[10px] text-zinc-500 text-center leading-relaxed">
            By accepting, you confirm that the work has been completed to your satisfaction.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
